"use client";

// Client-driven dispatch engine. Runs fully on Firestore (free Spark plan —
// no Cloud Functions required). Security is enforced by firestore.rules.

import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDoc,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { COMMISSION_RATE, PLATFORM_UPI, platformFeeFor } from "./billing";
import { generateJobCode } from "./job-security";

export type Coordinates = { latitude: number; longitude: number };

export type OpenRequest = {
  id: string;
  customerId: string;
  vehicle: string;
  problem: string;
  locationLabel: string;
  coordinates: Coordinates;
  createdAtMs: number;
  distanceKm?: number;
};

export type PartnerProfile = {
  id: string;
  name: string;
  initials: string;
  basePrice: number;
  rating: number;
  completedJobs: number;
  online: boolean;
  verified: boolean;
  suspended: boolean;
  subscriptionStatus: string;
};

export type ContactCard = { phone: string; name: string };

export const ACTIVE_STATUSES = ["confirmed", "en_route", "arrived", "in_progress"];

/** SOS cards older than this are treated as abandoned and hidden from mistris. */
export const BROADCAST_TTL_MS = 20 * 60 * 1000;

export function distanceKmBetween(from: Coordinates, to: Coordinates) {
  const radians = (degrees: number) => (degrees * Math.PI) / 180;
  const latitudeDelta = radians(to.latitude - from.latitude);
  const longitudeDelta = radians(to.longitude - from.longitude);
  const a = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(radians(from.latitude)) * Math.cos(radians(to.latitude))
    * Math.sin(longitudeDelta / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function etaMinutesForDistance(km: number) {
  return Math.max(5, Math.min(60, Math.round(km * 3.2 + 4)));
}

function requireUid() {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Sign in first");
  return uid;
}

// ---------------------------------------------------------------- partner side

export function watchPartnerProfile(uid: string, callback: (profile: PartnerProfile | null) => void) {
  return onSnapshot(doc(db, "mechanics", uid), (snapshot) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }
    const data = snapshot.data();
    callback({
      id: snapshot.id,
      name: String(data.name || "Mistry Bhai Partner"),
      initials: String(data.initials || "MB"),
      basePrice: Number(data.basePrice || 299),
      rating: Number(data.rating || 4.7),
      completedJobs: Number(data.completedJobs || 0),
      online: data.online === true,
      verified: data.verified === true,
      suspended: data.suspended === true,
      subscriptionStatus: String(data.subscriptionStatus || "active"),
    });
  });
}

/**
 * Partner profiles are created by admin approval. This only lets an already-approved
 * applicant finish local setup (base price) if the mechanic doc already exists.
 */
export async function activatePartnerProfile(input: { name: string; initials: string; basePrice: number }) {
  const uid = requireUid();
  const application = await getDoc(doc(db, "partnerApplications", uid));
  if (!application.exists() || application.get("verificationStatus") !== "approved") {
    throw new Error("Your application must be approved by Mistry Bhai admin before going live");
  }
  const existing = await getDoc(doc(db, "mechanics", uid));
  if (!existing.exists()) {
    throw new Error("Admin approval is still provisioning your profile — refresh in a minute");
  }
  await updateDoc(doc(db, "mechanics", uid), {
    name: input.name.slice(0, 80),
    initials: input.initials.slice(0, 3).toUpperCase() || "MB",
    basePrice: Math.max(49, Math.min(100000, Math.round(input.basePrice) || 299)),
    updatedAt: serverTimestamp(),
  });
  const userSnap = await getDoc(doc(db, "users", uid));
  const existingRole = userSnap.exists() ? String(userSnap.get("role") || "customer") : "customer";
  if (!["owner", "admin", "support"].includes(existingRole)) {
    await setDoc(doc(db, "users", uid), { role: "partner", updatedAt: serverTimestamp() }, { merge: true });
  }
}

export async function setPartnerOnline(online: boolean, basePrice?: number) {
  const uid = requireUid();
  const update: Record<string, unknown> = { online, updatedAt: serverTimestamp() };
  if (typeof basePrice === "number" && Number.isFinite(basePrice)) {
    update.basePrice = Math.max(49, Math.min(100000, Math.round(basePrice)));
  }
  await updateDoc(doc(db, "mechanics", uid), update);
}

export function watchOpenRequests(
  getLocation: () => Coordinates | null,
  radiusKm: number,
  callback: (requests: OpenRequest[]) => void,
  partnerUid?: string,
) {
  const openQuery = query(
    collection(db, "serviceRequests"),
    where("status", "==", "broadcasting"),
    orderBy("createdAt", "desc"),
    limit(30),
  );
  return onSnapshot(openQuery, (snapshot) => {
    const now = Date.now();
    const myLocation = getLocation();
    const rows = snapshot.docs.flatMap((item) => {
      const data = item.data();
      const customerId = String(data.customerId || "");
      if (partnerUid && customerId === partnerUid) return [];
      const point = data.coordinates as { latitude?: unknown; longitude?: unknown } | undefined;
      if (typeof point?.latitude !== "number" || typeof point?.longitude !== "number") return [];
      const createdAt = data.createdAt as { toMillis?: () => number } | undefined;
      const createdAtMs = createdAt?.toMillis?.();
      if (!createdAtMs || now - createdAtMs > BROADCAST_TTL_MS) return [];
      const coordinates = { latitude: point.latitude, longitude: point.longitude };
      const distanceKm = myLocation ? distanceKmBetween(myLocation, coordinates) : undefined;
      if (myLocation && distanceKm !== undefined && distanceKm > radiusKm) return [];
      return [{
        id: item.id,
        customerId,
        vehicle: String(data.vehicle || "Vehicle"),
        problem: String(data.problem || "Roadside help"),
        locationLabel: String(data.locationLabel || "Live location"),
        coordinates,
        createdAtMs,
        distanceKm: distanceKm !== undefined ? Math.round(distanceKm * 10) / 10 : undefined,
      }];
    });
    rows.sort((left, right) => (left.distanceKm ?? 999) - (right.distanceKm ?? 999));
    callback(rows);
  }, () => callback([]));
}

/** First mistri to accept wins — atomic so two partners can never both lock it. */
export async function acceptOpenRequest(requestId: string, quote: number, myLocation: Coordinates) {
  const uid = requireUid();
  const partnerSnapshot = await getDoc(doc(db, "mechanics", uid));
  if (!partnerSnapshot.exists() || partnerSnapshot.get("verified") !== true) {
    throw new Error("Activate your verified mistri profile first");
  }
  if (partnerSnapshot.get("suspended") === true) {
    throw new Error("Your account is suspended for an off-app policy violation");
  }
  const price = Math.max(49, Math.min(100000, Math.round(quote) || Number(partnerSnapshot.get("basePrice")) || 299));
  await runTransaction(db, async (transaction) => {
    const requestRef = doc(db, "serviceRequests", requestId);
    const request = await transaction.get(requestRef);
    if (!request.exists() || request.get("status") !== "broadcasting" || request.get("partnerId")) {
      throw new Error("Another mistri already accepted this request");
    }
    const point = request.get("coordinates") as Coordinates;
    const distance = Math.round(distanceKmBetween(myLocation, point) * 10) / 10;
    transaction.update(requestRef, {
      status: "partner_accepted",
      partnerId: uid,
      helperName: String(partnerSnapshot.get("name") || "Mistry Bhai Partner").slice(0, 80),
      helperInitials: String(partnerSnapshot.get("initials") || "MB").slice(0, 3),
      helperRating: Math.max(0, Math.min(5, Number(partnerSnapshot.get("rating") || 4.7))),
      helperJobs: Math.max(0, Number(partnerSnapshot.get("completedJobs") || 0)),
      price,
      etaMinutes: etaMinutesForDistance(distance),
      partnerDistanceKm: distance,
      partnerLocation: myLocation,
      acceptedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
  return price;
}

export async function updateJobStatus(
  requestId: string,
  status: "en_route" | "arrived" | "in_progress" | "completed",
  jobCode?: string,
) {
  const uid = requireUid();
  const allowedNext: Record<string, string[]> = {
    confirmed: ["en_route"],
    en_route: ["arrived"],
    arrived: ["in_progress"],
    in_progress: ["completed"],
  };

  if (status !== "completed") {
    await runTransaction(db, async (transaction) => {
      const requestRef = doc(db, "serviceRequests", requestId);
      const request = await transaction.get(requestRef);
      if (!request.exists() || request.get("partnerId") !== uid) throw new Error("Not your booking");
      const current = String(request.get("status") || "");
      if (!allowedNext[current]?.includes(status)) {
        throw new Error(`Complete "${current.replace(/_/g, " ")}" step first`);
      }
      const storedCode = request.get("jobCode");
      if ((status === "arrived" || status === "in_progress") && storedCode) {
        if (!jobCode || jobCode.trim() !== String(storedCode)) {
          throw new Error("Wrong code — ask the customer for their 4-digit job code");
        }
      }
      transaction.update(requestRef, {
        status,
        updatedAt: serverTimestamp(),
        ...(status === "arrived" ? { arrivalVerifiedAt: serverTimestamp() } : {}),
        ...(status === "in_progress" ? { workStartedAt: serverTimestamp() } : {}),
      });
      transaction.set(doc(db, "serviceRequests", requestId, "events", `${Date.now()}_${status}`), {
        type: "status",
        status,
        byUid: uid,
        byRole: "partner",
        at: serverTimestamp(),
      });
    });
    return;
  }

  // Finishing a job also ticks the partner's public job counter, exactly once.
  await runTransaction(db, async (transaction) => {
    const requestRef = doc(db, "serviceRequests", requestId);
    const request = await transaction.get(requestRef);
    if (!request.exists() || request.get("partnerId") !== uid) throw new Error("Not your booking");
    if (request.get("status") === "completed") return;
    if (request.get("status") !== "in_progress") throw new Error("Tap Start work before Job done");
    transaction.update(requestRef, {
      status,
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    transaction.set(doc(db, "serviceRequests", requestId, "events", `${Date.now()}_completed`), {
      type: "status",
      status: "completed",
      byUid: uid,
      byRole: "partner",
      at: serverTimestamp(),
    });
    transaction.update(doc(db, "mechanics", uid), {
      completedJobs: increment(1),
      updatedAt: serverTimestamp(),
    });
    const jobAmount = Number(request.get("price") || 0);
    const fee = platformFeeFor(jobAmount);
    transaction.set(doc(db, "commissionDues", requestId), {
      partnerId: uid,
      requestId,
      customerId: String(request.get("customerId") || ""),
      jobAmount,
      amount: fee,
      rate: COMMISSION_RATE,
      currency: "INR",
      status: "due",
      payeeUpi: PLATFORM_UPI,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
}

export async function partnerCancelAcceptance(requestId: string) {
  const uid = requireUid();
  await runTransaction(db, async (transaction) => {
    const requestRef = doc(db, "serviceRequests", requestId);
    const request = await transaction.get(requestRef);
    if (!request.exists() || request.get("partnerId") !== uid) throw new Error("Not your booking");
    transaction.update(requestRef, {
      status: "broadcasting",
      partnerId: deleteField(),
      helperName: deleteField(),
      helperInitials: deleteField(),
      helperRating: deleteField(),
      helperJobs: deleteField(),
      price: deleteField(),
      etaMinutes: deleteField(),
      partnerDistanceKm: deleteField(),
      partnerLocation: deleteField(),
      updatedAt: serverTimestamp(),
    });
  });
}

export function watchMyAssignedRequest(callback: (request: (OpenRequest & {
  status: string;
  price?: number;
  paymentMethod?: string;
  paymentStatus?: string;
  jobCode?: string;
}) | null) => void) {
  const uid = requireUid();
  const assignedQuery = query(
    collection(db, "serviceRequests"),
    where("partnerId", "==", uid),
    orderBy("updatedAt", "desc"),
    limit(5),
  );
  return onSnapshot(assignedQuery, (snapshot) => {
    const active = snapshot.docs.find((item) => {
      const status = String(item.data().status || "");
      return ["partner_accepted", ...ACTIVE_STATUSES, "cancelled"].includes(status);
    });
    if (!active) {
      callback(null);
      return;
    }
    const data = active.data();
    const point = data.coordinates as Coordinates;
    callback({
      id: active.id,
      customerId: String(data.customerId || ""),
      vehicle: String(data.vehicle || "Vehicle"),
      problem: String(data.problem || "Roadside help"),
      locationLabel: String(data.locationLabel || "Live location"),
      coordinates: point,
      createdAtMs: Date.now(),
      status: String(data.status || ""),
      price: typeof data.price === "number" ? data.price : undefined,
      paymentMethod: data.paymentMethod ? String(data.paymentMethod) : undefined,
      paymentStatus: data.paymentStatus ? String(data.paymentStatus) : undefined,
      jobCode: data.jobCode ? String(data.jobCode) : undefined,
    });
  }, () => callback(null));
}

// ------------------------------------------------------------------ tracking

/** Both parties write their own live GPS doc under the request. */
export async function publishLiveLocation(
  requestId: string,
  role: "partner" | "customer",
  point: Coordinates & { accuracy?: number; heading?: number },
) {
  const uid = requireUid();
  await setDoc(doc(db, "serviceRequests", requestId, "tracking", uid), {
    role,
    latitude: point.latitude,
    longitude: point.longitude,
    accuracy: typeof point.accuracy === "number" ? Math.max(0, point.accuracy) : null,
    heading: typeof point.heading === "number" ? point.heading : null,
    updatedAt: serverTimestamp(),
  });
  if (role === "partner") {
    await updateDoc(doc(db, "serviceRequests", requestId), {
      partnerLocation: { latitude: point.latitude, longitude: point.longitude },
      updatedAt: serverTimestamp(),
    }).catch(() => undefined);
  }
}

export function watchLiveLocations(
  requestId: string,
  callback: (points: Record<string, Coordinates & { role: string; updatedAtMs: number }>) => void,
) {
  return onSnapshot(collection(db, "serviceRequests", requestId, "tracking"), (snapshot) => {
    const points: Record<string, Coordinates & { role: string; updatedAtMs: number }> = {};
    snapshot.docs.forEach((item) => {
      const data = item.data();
      if (typeof data.latitude !== "number" || typeof data.longitude !== "number") return;
      const updatedAt = data.updatedAt as { toMillis?: () => number } | undefined;
      points[item.id] = {
        latitude: data.latitude,
        longitude: data.longitude,
        role: String(data.role || "partner"),
        updatedAtMs: updatedAt?.toMillis?.() || 0,
      };
    });
    callback(points);
  }, () => callback({}));
}

/** Continuous device GPS -> Firestore publisher. Returns a stop function. */
export function startLocationPublisher(requestId: string, role: "partner" | "customer") {
  if (!navigator.geolocation) return () => undefined;
  let lastSentAt = 0;
  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      const now = Date.now();
      if (now - lastSentAt < 4000) return;
      lastSentAt = now;
      void publishLiveLocation(requestId, role, {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        heading: position.coords.heading ?? undefined,
      }).catch(() => undefined);
    },
    () => undefined,
    { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 },
  );
  return () => navigator.geolocation.clearWatch(watchId);
}

// ------------------------------------------------------ confirm & contact unlock

export async function confirmRequest(requestId: string, paymentMethod: "cash" | "upi_on_service" = "upi_on_service") {
  const uid = requireUid();
  await runTransaction(db, async (transaction) => {
    const requestRef = doc(db, "serviceRequests", requestId);
    const request = await transaction.get(requestRef);
    if (!request.exists() || request.get("customerId") !== uid) throw new Error("Not your request");
    if (request.get("status") !== "partner_accepted") throw new Error("Wait for a mistri to accept first");
    transaction.update(requestRef, {
      status: "confirmed",
      contactUnlocked: true,
      priceAgreedByCustomer: true,
      paymentMethod,
      paymentStatus: "pay_on_site",
      jobCode: generateJobCode(),
      confirmedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    transaction.set(doc(db, "serviceRequests", requestId, "events", `${Date.now()}_confirmed`), {
      type: "status",
      status: "confirmed",
      byUid: uid,
      byRole: "customer",
      at: serverTimestamp(),
    });
  });
}

export async function markPaymentCollected(requestId: string) {
  const uid = requireUid();
  await updateDoc(doc(db, "serviceRequests", requestId), {
    paymentStatus: "collected",
    paidAt: serverTimestamp(),
    paidByUid: uid,
    updatedAt: serverTimestamp(),
  });
}

export async function revertPaymentCollected(requestId: string) {
  const uid = requireUid();
  await updateDoc(doc(db, "serviceRequests", requestId), {
    paymentStatus: "pay_on_site",
    paidAt: deleteField(),
    paidByUid: deleteField(),
    paymentRevertByUid: uid,
    updatedAt: serverTimestamp(),
  });
}

export async function cancelRequest(requestId: string, reason: string) {
  const uid = requireUid();
  await runTransaction(db, async (transaction) => {
    const requestRef = doc(db, "serviceRequests", requestId);
    const request = await transaction.get(requestRef);
    if (!request.exists() || request.get("customerId") !== uid) throw new Error("Not your request");
    transaction.update(requestRef, {
      status: "cancelled",
      cancelReason: reason.slice(0, 200),
      cancelledAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
}

/** Share my phone card after confirmation. Rules only allow this when status is confirmed+. */
export async function shareMyContact(requestId: string, phone: string, name: string) {
  const uid = requireUid();
  const clean = phone.replace(/\D/g, "").slice(-10);
  if (clean.length !== 10) throw new Error("Enter a valid 10-digit phone number");
  await setDoc(doc(db, "serviceRequests", requestId, "contacts", uid), {
    phone: `+91${clean}`,
    name: name.slice(0, 80),
    sharedAt: serverTimestamp(),
  });
}

export function watchContacts(requestId: string, callback: (contacts: Record<string, ContactCard>) => void) {
  return onSnapshot(collection(db, "serviceRequests", requestId, "contacts"), (snapshot) => {
    const contacts: Record<string, ContactCard> = {};
    snapshot.docs.forEach((item) => {
      contacts[item.id] = {
        phone: String(item.data().phone || ""),
        name: String(item.data().name || ""),
      };
    });
    callback(contacts);
  }, () => callback({}));
}

// ------------------------------------------------------------- fraud protection

export type FraudVerdict = {
  flagged: boolean;
  reason?: string;
  closestApproachKm?: number;
};

/**
 * After a cancellation, watch whether the previously-assigned mistri keeps
 * approaching the breakdown point (classic "cancel the app booking, I'll come
 * anyway" off-app deal). If he closes in, flag him: suspend account, cancel
 * subscription, record a penalty deduction of the agreed job amount.
 */
export function startCancelFraudWatch(input: {
  requestId: string;
  partnerId: string;
  customerLocation: Coordinates;
  distanceAtCancelKm: number;
  agreedPrice: number;
  onVerdict: (verdict: FraudVerdict) => void;
}) {
  const APPROACH_KM = 0.4;
  const WATCH_MS = 15 * 60 * 1000;
  let closest = input.distanceAtCancelKm;
  let done = false;

  const stopTracking = watchLiveLocations(input.requestId, (points) => {
    const partnerPoint = points[input.partnerId];
    if (!partnerPoint || done) return;
    const distance = distanceKmBetween(partnerPoint, input.customerLocation);
    if (distance < closest) closest = distance;
    if (distance <= APPROACH_KM && input.distanceAtCancelKm > APPROACH_KM) {
      done = true;
      void flagOffAppFraud({
        requestId: input.requestId,
        partnerId: input.partnerId,
        reason: "partner_approached_after_cancellation",
        evidenceKm: Math.round(distance * 100) / 100,
        agreedPrice: input.agreedPrice,
      }).then(() => input.onVerdict({ flagged: true, reason: "Mistri kept coming after cancellation", closestApproachKm: distance }))
        .catch(() => input.onVerdict({ flagged: false }));
    }
  });

  const timer = window.setTimeout(() => {
    if (!done) input.onVerdict({ flagged: false, closestApproachKm: closest });
    stopTracking();
  }, WATCH_MS);

  return () => {
    window.clearTimeout(timer);
    stopTracking();
  };
}

/** Creates the fraud flag, suspends the mistri, cancels his subscription and records the deduction. */
export async function flagOffAppFraud(input: {
  requestId: string;
  partnerId: string;
  reason: string;
  evidenceKm?: number;
  agreedPrice: number;
}) {
  const uid = requireUid();
  await setDoc(doc(db, "fraudFlags", input.requestId), {
    requestId: input.requestId,
    partnerId: input.partnerId,
    customerId: uid,
    reason: input.reason,
    evidenceKm: input.evidenceKm ?? null,
    agreedPrice: input.agreedPrice,
    status: "auto_enforced",
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, "mechanics", input.partnerId), {
    suspended: true,
    online: false,
    subscriptionStatus: "cancelled",
    suspensionReason: input.reason,
    fraudRequestId: input.requestId,
    updatedAt: serverTimestamp(),
  });
  await addDoc(collection(db, "penaltyDeductions"), {
    partnerId: input.partnerId,
    requestId: input.requestId,
    customerId: uid,
    amount: input.agreedPrice,
    currency: "INR",
    reason: input.reason,
    status: "deduction_scheduled",
    createdAt: serverTimestamp(),
  });
}
